// src/components/SearchFilters.jsx
import React from "react";

/**
 * Simple Make / Model filter bar.
 *
 * Props:
 * - allProducts: Array<{ make: string, model: string }>
 * - value: { make: string, model: string }
 * - onChange: (next: { make: string, model: string }) => void
 */
export default function SearchFilters({
  allProducts = [],
  value = { make: "", model: "" },
  onChange,
}) {
  // Unique makes from data
  const makes = React.useMemo(
    () => Array.from(new Set(allProducts.map(p => p.make).filter(Boolean))).sort(),
    [allProducts]
  );

  // Models limited by selected make (or all if make not chosen)
  const models = React.useMemo(
    () =>
      Array.from(
        new Set(
          allProducts
            .filter(p => !value.make || p.make === value.make)
            .map(p => p.model)
            .filter(Boolean)
        )
      ).sort(),
    [allProducts, value.make]
  );

  const handleMake = (e) => {
    const make = e.target.value;
    // Clear model when make changes so model list stays valid
    onChange?.({ make, model: "" });
  };

  const handleModel = (e) => {
    onChange?.({ ...value, model: e.target.value });
  };

  const reset = () => onChange?.({ make: "", model: "" });

  return (
    <div className="mb-6 grid sm:grid-cols-3 gap-3 items-end">
      <div>
        <label htmlFor="filter-make" className="block text-sm font-semibold mb-1">
          Make
        </label>
        <select
          id="filter-make"
          className="w-full rounded-xl border border-neutral-300 px-3 py-2"
          value={value.make}
          onChange={handleMake}
        >
          <option value="">All</option>
          {makes.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="filter-model" className="block text-sm font-semibold mb-1">
          Model
        </label>
        <select
          id="filter-model"
          className="w-full rounded-xl border border-neutral-300 px-3 py-2"
          value={value.model}
          onChange={handleModel}
          disabled={models.length === 0}
        >
          <option value="">All</option>
          {models.map((md) => (
            <option key={md} value={md}>{md}</option>
          ))}
        </select>
      </div>

      <button
        type="button"
        onClick={reset}
        className="h-[42px] rounded-xl border border-neutral-300 hover:bg-neutral-100 transition"
      >
        Reset
      </button>
    </div>
  );
}
